import * as React from "react";
import { connect } from "common/connect";
import StoreKeys from "common/storeKeys";
import EventService from "services/EventService";
import Store from "common/store";
import {
	Button,
	Container,
	FormControl,
	FormControlLabel,
	MenuItem,
	Paper,
	Select,
	Switch,
	Typography,
	makeStyles,
} from "@material-ui/core";
import { Colors } from "styles/Colors";
import { SetLoading } from "common/components/Loader/Loader";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";
import { games, GetCurrentGameId } from "common/constants/Games";
import { RewardTypes } from "common/constants/RewardTypes";
import { changeUrl } from "common/history";
import LookupService from "services/LookupService";
import EventStatusCard from "./EventStatusCard";

const propKeys = {
	events: StoreKeys.ADMIN.EVENT_MANAGER.EVENTS,
	selectedGame: StoreKeys.ADMIN.EVENT_MANAGER.SELECTED_GAME,
	diagnostics: StoreKeys.ADMIN.EVENT_MANAGER.DIAGNOSTICS,
};

const gamePrefix = (gameId) => games.find((g) => g.id === gameId)?.prefix ?? "";

const useStyles = makeStyles((theme) => ({
	root: {
		paddingTop: theme.spacing(4),
		paddingBottom: theme.spacing(6),
	},
	header: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: theme.spacing(2),
		flexWrap: "wrap",
		marginBottom: theme.spacing(2),
	},
	headerActions: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(2),
	},
	gameSelect: {
		minWidth: 160,
		"& .MuiSelect-nativeInput": { margin: 0, padding: 0, border: 0 },
	},
	row: {
		backgroundColor: Colors.panel,
		padding: theme.spacing(3),
		marginBottom: theme.spacing(2),
	},
	rowHead: {
		display: "flex",
		alignItems: "flex-start",
		justifyContent: "space-between",
		gap: theme.spacing(2),
		marginBottom: theme.spacing(1),
	},
	title: { color: Colors.primary },
	// Cancels the Switch's own padding so it stays level with the top-aligned title.
	hiddenToggle: { marginTop: -6 },
	body: {
		display: "flex",
		alignItems: "flex-start",
		gap: theme.spacing(4),
	},
	fields: { flex: 1, minWidth: 0 },
	field: {
		display: "flex",
		flexDirection: "column",
		marginBottom: theme.spacing(1),
	},
	label: { fontSize: 12, opacity: 0.8, marginBottom: 2 },
	input: {
		width: "100%",
		boxSizing: "border-box",
		margin: 0,
		padding: 6,
		color: Colors.primary,
		backgroundColor: Colors.panelDark,
		border: `1px solid ${Colors.panelLight}`,
		borderRadius: 4,
	},
	rewardField: { marginTop: theme.spacing(2) },
	// Insets the value to line up with the text inputs above, which pad inside their border.
	rewardSelect: {
		minWidth: 160,
		alignSelf: "flex-start",
		"& .MuiSelect-select": { paddingLeft: 7 },
	},
	preview: { maxWidth: 300, flexShrink: 0 },
	actions: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(2),
		marginTop: theme.spacing(3),
	},
	open: { color: Colors.pickstop, marginLeft: "auto" },
}));

const toastError = (error, fallback) =>
	toast(error?.message?.trim() || fallback, {
		type: "error",
	});

const NOTIFY_RESULTS = {
	Sent: "Stage-open push sent.",
	AlreadySent: "Already sent for this stage.",
	SuppressedTooLate: "Picks lock too soon. Push skipped and marked as handled.",
	Hidden: "Event is hidden. It will notify once unhidden.",
	NoCurrentStage: "Event has no current stage.",
};

async function loadEvents(gameId) {
	try {
		SetLoading(true, "Loading Events");
		const loaded = await EventService.getManagedEvents(gameId);
		Store.set(StoreKeys.ADMIN.EVENT_MANAGER.EVENTS, loaded);
	} catch (error) {
		toastError(error, "Failed to load events.");
	} finally {
		SetLoading(false);
	}
}

async function loadDiagnostics(eventId) {
	try {
		SetLoading(true, "Loading Status");
		Store.set(
			StoreKeys.ADMIN.EVENT_MANAGER.DIAGNOSTICS,
			await EventService.getDiagnostics(eventId)
		);
	} catch (error) {
		toastError(error, "Failed to load event status.");
	} finally {
		SetLoading(false);
	}
}

function EventRow({ event, classes, onChanged, onToggleStatus }) {
	const [name, setName] = React.useState(event.nameOverride ?? "");
	const [image, setImage] = React.useState(event.image ?? "");
	const [hidden, setHidden] = React.useState(event.hidden === true);
	const [rewardTypeId, setRewardTypeId] = React.useState(
		event.rewardTypeId ?? ""
	);

	React.useEffect(() => {
		setName(event.nameOverride ?? "");
		setImage(event.image ?? "");
		setHidden(event.hidden === true);
		setRewardTypeId(event.rewardTypeId ?? "");
	}, [event.nameOverride, event.image, event.hidden, event.rewardTypeId]);

	const persist = async (nextHidden) => {
		try {
			SetLoading(true, "Saving");
			await EventService.updateEventDetails({
				eventId: event.id,
				nameOverride: name,
				image,
				hidden: nextHidden,
				rewardTypeId: rewardTypeId === "" ? null : rewardTypeId,
			});
			toast("Saved.", { type: "success" });
			onChanged();
			return true;
		} catch (error) {
			toastError(error, "Save failed.");
			return false;
		} finally {
			SetLoading(false);
		}
	};

	const toggleHidden = async (e) => {
		const next = e.target.checked;
		setHidden(next);
		if (!(await persist(next))) {
			setHidden(!next);
		}
	};

	return (
		<Paper className={classes.row} elevation={2}>
			<div className={classes.rowHead}>
				<Typography className={classes.title}>
					{event.nameOverride || event.name} (#{event.id})
				</Typography>
				<FormControlLabel
					className={classes.hiddenToggle}
					control={
						<Switch checked={hidden} onChange={toggleHidden} color="primary" />
					}
					label="Hidden"
				/>
			</div>
			<div className={classes.body}>
				<div className={classes.fields}>
					<div className={classes.field}>
						<label className={classes.label}>Name</label>
						<input
							className={classes.input}
							value={name}
							maxLength={100}
							onChange={(e) => setName(e.target.value)}
						/>
					</div>
					<div className={classes.field}>
						<label className={classes.label}>Image URL</label>
						<input
							className={classes.input}
							value={image}
							maxLength={100}
							onChange={(e) => setImage(e.target.value)}
						/>
					</div>
					<div className={`${classes.field} ${classes.rewardField}`}>
						<label className={classes.label}>Reward Type</label>
						<Select
							className={classes.rewardSelect}
							value={rewardTypeId}
							onChange={(e) => setRewardTypeId(e.target.value)}
							color="primary"
							displayEmpty
						>
							<MenuItem value="">None</MenuItem>
							{Object.keys(RewardTypes.getRewardTypes(event.gameId)).map(
								(key) => (
									<MenuItem key={RewardTypes[key]} value={RewardTypes[key]}>
										{key}
									</MenuItem>
								)
							)}
						</Select>
					</div>
				</div>
				{image ? <img src={image} alt="" className={classes.preview} /> : null}
			</div>
			<div className={classes.actions}>
				<Button
					variant="contained"
					color="primary"
					onClick={() => persist(hidden)}
				>
					Save
				</Button>
				<Button
					variant="outlined"
					color="primary"
					onClick={() => onToggleStatus(event.id)}
				>
					Status
				</Button>
				<a
					className={classes.open}
					href={`/${gamePrefix(event.gameId)}/event/${event.id}`}
					target="_blank"
					rel="noopener noreferrer"
				>
					View public page
				</a>
			</div>
		</Paper>
	);
}

function EventManagerC({ events, selectedGame, diagnostics }) {
	const classes = useStyles();
	const gameId = selectedGame || GetCurrentGameId();
	const [gameOptions, setGameOptions] = React.useState(null);

	React.useEffect(() => {
		LookupService.getGames().then(setGameOptions);
	}, []);

	React.useEffect(() => {
		if (!selectedGame) {
			Store.set(StoreKeys.ADMIN.EVENT_MANAGER.SELECTED_GAME, gameId);
		}
		loadEvents(gameId);
		Store.set(StoreKeys.ADMIN.EVENT_MANAGER.DIAGNOSTICS, null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [gameId]);

	const onGameChange = (e) => {
		Store.set(
			StoreKeys.ADMIN.EVENT_MANAGER.SELECTED_GAME,
			Number(e.target.value)
		);
	};

	const reload = () => loadEvents(gameId);

	const toggleStatus = (eventId) =>
		diagnostics?.id === eventId
			? Store.set(StoreKeys.ADMIN.EVENT_MANAGER.DIAGNOSTICS, null)
			: loadDiagnostics(eventId);

	const forceSync = async (eventId) => {
		try {
			SetLoading(true, "Syncing");
			await EventService.syncNow(eventId);
			toast("Sync complete.", { type: "success" });
		} catch (error) {
			toastError(error, "Sync failed - check logs.");
			return;
		} finally {
			SetLoading(false);
		}
		await loadDiagnostics(eventId);
		await loadEvents(gameId);
	};

	const notifyStageOpen = async (eventId) => {
		try {
			SetLoading(true, "Sending");
			const result = await EventService.notifyStageOpen(eventId);
			toast(NOTIFY_RESULTS[result] ?? `Result: ${result}`, {
				type: result === "Sent" ? "success" : "info",
			});
		} catch (error) {
			toastError(error, "Send failed - check logs.");
			return;
		} finally {
			SetLoading(false);
		}
		await loadDiagnostics(eventId);
	};

	const hasEvents = Array.isArray(events) && events.length > 0;

	return (
		<Container maxWidth="md" className={classes.root}>
			<Helmet>
				<title>Pickstop - Event Manager</title>
			</Helmet>
			<div className={classes.header}>
				<Typography variant="h5">Event Manager</Typography>
				<div className={classes.headerActions}>
					<FormControl className={classes.gameSelect}>
						<Select value={gameId} onChange={onGameChange} color="primary">
							{(gameOptions ?? []).map((g) => (
								<MenuItem key={g.id} value={g.id}>
									{g.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<Button
						variant="contained"
						color="primary"
						onClick={() => changeUrl("/admin/event/create")}
					>
						Create Event
					</Button>
				</div>
			</div>
			{hasEvents ? (
				events.map((e) => (
					<React.Fragment key={e.id}>
						<EventRow
							event={e}
							classes={classes}
							onChanged={reload}
							onToggleStatus={toggleStatus}
						/>
						{diagnostics?.id === e.id ? (
							<EventStatusCard
								diagnostics={diagnostics}
								onForceSync={() => forceSync(e.id)}
								onNotifyStageOpen={() => notifyStageOpen(e.id)}
							/>
						) : null}
					</React.Fragment>
				))
			) : (
				<Typography>There are no events for this game.</Typography>
			)}
		</Container>
	);
}

export default function EventManager(props) {
	return connect(<EventManagerC />, propKeys, props);
}
